import { STUDIO_PROD_URL, STUDIO_STAGING_URL } from './constants';

type StudioEnv = 'production' | 'staging';

const getStudioHost = (env: StudioEnv): string =>
	env === 'staging' ? STUDIO_STAGING_URL : STUDIO_PROD_URL;

/**
 * Builds Studio entry URLs for syncing account context with Studio via `login_hint` param.
 *
 * @param env - `'production'` or `'staging'`
 * @param path - Relative Studio path (e.g. `'/s/site-id/agents'`), with or without a leading slash,
 *   or `null`/`undefined`/`''` to get the Studio base URL for the given env.
 *   Do NOT pass an already-absolute URL — always use a relative path.
 * @param email - The user's email address used as `login_hint` for account alignment.
 * @returns `getStudioSessionSyncUrl('production', '/s/site/agents', 'user@example.com')` →
 *   `https://studio.atlassian.com/s/site/agents?login_hint=user%40example.com` (omit email for no query).
 * @see https://hello.atlassian.net/wiki/spaces/~63749c0c213a315af3456393/pages/6708435317/RFC+Atlassian+Identity+Session+Synchronization+Issues+with+Multi-Account+Navigation+in+Studio
 */
export const getStudioSessionSyncUrl = (
	env: StudioEnv,
	path: string | null | undefined,
	email: string | null | undefined,
): string => {
	const studioHost = getStudioHost(env);
	const normalizedPath = path ? (path.startsWith('/') ? path : `/${path}`) : '';
	const destination = `${studioHost}${normalizedPath}`;

	if (!email) {
		return destination;
	}

	// Merge login_hint into any existing query string.
	const url = new URL(destination);
	url.searchParams.set('login_hint', email);
	return url.toString();
};
